import { useEffect, useState } from "react";
import { CloudOff, RefreshCw, AlertTriangle } from "lucide-react";
import { estadoFila, flushQueue, mostrarSituacaoFila, pendingCount } from "@/lib/offlineQueue";

const ESTADO_VAZIO = { total: 0, bloqueados: 0, vendedores_bloqueados: [], avisos: 0 };
function lerEstado() {
  try { return estadoFila(); } catch { return ESTADO_VAZIO; }
}
function tentarEnviar() {
  if (navigator.onLine && pendingCount() > 0) flushQueue();
}

// Avisos: venda que saiu da fila sem sincronizar, ou que sincronizou com valor ajustado ou com
// ocorrência registrada (G6; a ocorrência mantém o desconto, sem diferença a cobrar).
function rotuloAvisos(fila) {
  const n = fila.avisos;
  const ajustes = Math.min(Math.max(0, fila.avisos_ajuste || 0), n);
  const ocorrencias = Math.min(Math.max(0, fila.avisos_ocorrencia || 0), n);
  const s = n === 1 ? "" : "s";
  if (!ajustes && !ocorrencias) return `não sincronizada${s}`;
  if (ajustes === n) return "com valor ajustado";
  if (ocorrencias === n) return `com ocorrência${n === 1 ? "" : "s"}`;
  return "com aviso";
}

export default function OfflineIndicator() {
  const [online, setOnline] = useState(navigator.onLine);
  const [fila, setFila] = useState(lerEstado);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    const refresh = () => setFila(lerEstado());
    refresh();
    window.addEventListener("offline-queue-changed", refresh);
    const interval = setInterval(refresh, 5000);
    // 4.12: reenvia sozinho enquanto houver venda guardada (antes só no evento "online": com a
    // página recarregada e a fila parada, o "Sincronizando" girava para sempre).
    const primeira = setTimeout(tentarEnviar, 2000);
    const retry = setInterval(tentarEnviar, 30000);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("offline-queue-changed", refresh);
      clearInterval(interval);
      clearTimeout(primeira);
      clearInterval(retry);
    };
  }, []);

  const pending = fila.total;
  // Venda aguardando login nunca sincroniza sozinha: não entra no "Sincronizando" e o botão
  // dela aparece sempre que houver uma, mesmo junto com vendas pendentes por rede/5xx.
  const bloqueados = Math.min(Math.max(0, fila.bloqueados || 0), pending);
  const sincronizando = pending - bloqueados;
  if (online && pending === 0 && fila.avisos === 0) return null;

  return (
    <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[60] flex flex-col items-center gap-1.5">
      {(!online || sincronizando > 0) && (
        <div data-testid="offline-indicator"
             className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium shadow-lg"
             style={{
               background: online ? "linear-gradient(135deg,#F59E0B 0%,#F97316 100%)" : "linear-gradient(135deg,#0F172A 0%,#334155 100%)",
               color: "#fff",
             }}>
          {online ? (
            <>
              <RefreshCw size={11} className="animate-spin" /> Sincronizando {sincronizando}
            </>
          ) : (
            <>
              <CloudOff size={11} /> Offline · {pending > 0 && `${pending} fila`}
            </>
          )}
        </div>
      )}
      {bloqueados > 0 && (
        <button type="button" data-testid="offline-aguardando-login" onClick={() => mostrarSituacaoFila("bloqueados")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium shadow-lg text-white"
                style={{ background: "linear-gradient(135deg,#B45309 0%,#C2410C 100%)" }}>
          <AlertTriangle size={11} />
          {bloqueados} venda{bloqueados === 1 ? "" : "s"} aguardando login
          {fila.vendedores_bloqueados.length ? ` de ${fila.vendedores_bloqueados.join(", ")}` : ""}
        </button>
      )}
      {fila.avisos > 0 && (
        <button type="button" data-testid="offline-avisos" onClick={() => mostrarSituacaoFila()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium shadow-lg text-white"
                style={{ background: "linear-gradient(135deg,#BE123C 0%,#E11D48 100%)" }}>
          <AlertTriangle size={11} />
          {fila.avisos} venda{fila.avisos === 1 ? "" : "s"} {rotuloAvisos(fila)}: conferir
        </button>
      )}
    </div>
  );
}
